import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BiotechIcon from '@mui/icons-material/Biotech';
import {
  Box,
  Button,
  Menu,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { LdtAppRepository } from '@ldt/api/app_repository';
import { AuthenticationService } from 'src/api/authentication_service';
import { ChevronRight } from 'src/components/icons/chevron_right';
import { SearchInput } from 'src/components/search_input';
import { VirtualizedList } from 'src/components/virtualized_list';
import { organizationKeys } from 'src/constants/organizations';
import {
  useEffectiveOrganization,
  useIsLdtSalesOrCustomerSuccessUser,
} from 'src/hooks/use_auth';
import {
  OrganizationListFieldsFragment,
  OrganizationWorkflowType,
} from 'src/types';

// Styled Components
// Desktop version: hidden on xs/sm, shown on md+ for clinicians
const OrganizationContainer = styled(Box)(({ theme }) => ({
  color: 'inherit',
  width: 250,
  maxWidth: '100%',
  minWidth: 0,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  display: 'none',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

// Drawer version: shown on xs/sm, hidden on md+ for clinicians
const OrganizationContainerDrawer = styled(Box)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  minWidth: 0,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

// Desktop version: hidden on xs/sm, shown on md+ for CS/Sales
const OrganizationButton = styled(Button)(({ theme }) => ({
  color: 'inherit',
  textTransform: 'none',
  width: 250,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  display: 'none',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Drawer version: shown on xs/sm only, hidden on md+ for CS/Sales
const OrganizationButtonDrawer = styled(Button)(({ theme }) => ({
  color: 'inherit',
  textTransform: 'none',
  width: '100%',
  padding: `0 ${theme.spacing(1.5)}`,
  display: 'flex',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DisplayContentBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 8, // theme.spacing(1)
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  flex: 1,
});

const OrganizationLabel = styled(Typography)({
  display: 'block',
  lineHeight: 1,
});

/** Name + optional Biotech icon; flex so the name truncates with ellipsis when space is tight. */
const OrganizationNameRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
});

const OrganizationName = styled(Typography)({
  textAlign: 'left',
  lineHeight: 1,
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
});

const StyledChevronRight = styled(ChevronRight, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  color: theme.palette.text.secondary,
  transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
  transition: 'transform 0.2s ease-in-out',
  fontSize: '1.25rem',
  flexShrink: 0, // Prevent icon from shrinking
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const ListContainer = styled(Box)({
  maxHeight: 300,
  overflow: 'hidden',
});

const LoadingBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
});

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: `0 ${theme.spacing(2)}`,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
  margin: `0 ${theme.spacing(1)}`,
  minWidth: 0,
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const OrganizationListItemText = styled(ListItemText)({
  minWidth: 0,
  overflow: 'hidden',
});

function isDirectUploadLabOrganization(
  org?: Pick<OrganizationListFieldsFragment, 'workflowType'> | null,
): boolean {
  return org?.workflowType === OrganizationWorkflowType.DirectUploadLab;
}

interface OrganizationHeaderProps {
  appRepository: LdtAppRepository;
  authenticationService: AuthenticationService;
  onMobileDrawerClose?: () => void;
  variant?: 'desktop' | 'drawer';
}

export const OrganizationHeader: React.FC<OrganizationHeaderProps> = ({
  appRepository,
  onMobileDrawerClose,
  variant = 'desktop',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { organization: organizationParam } = useParams();
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const userOrganization = useEffectiveOrganization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const open = Boolean(anchorEl);

  const { data: organizations, isLoading } = useQuery<
    OrganizationListFieldsFragment[]
  >({
    queryKey: organizationKeys.lists,
    queryFn: async () => {
      return appRepository.getOrganizations();
    },
    enabled: isCSOrSalesUser, // Only fetch for CS/Sales users
  });

  // Filter organizations based on search term
  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];
    if (!searchTerm.trim()) {
      return organizations;
    }
    const term = searchTerm.toLowerCase();
    return organizations.filter(
      (org) =>
        org.name?.toLowerCase().includes(term) ||
        org.displayName?.toLowerCase().includes(term),
    );
  }, [organizations, searchTerm]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm(''); // Clear search when closing
  };

  const handleOrganizationSelect = useCallback(
    (organization: OrganizationListFieldsFragment) => {
      if (!organization.name) {
        throw new Error('Organization is missing a name');
      }

      handleClose();
      if (onMobileDrawerClose) {
        onMobileDrawerClose();
      }
      navigate(`/${organization.name}/orders`);
    },
    [navigate, onMobileDrawerClose],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const selectedOrganization = useMemo(
    () => organizations?.find((org) => org.name === organizationParam),
    [organizations, organizationParam],
  );

  const displayedOrganization = isCSOrSalesUser
    ? selectedOrganization
    : userOrganization;

  const displayName = displayedOrganization?.displayName;

  const showBiotechIconForDisplayedOrg = isDirectUploadLabOrganization(
    displayedOrganization,
  );

  // Shared display content
  const displayContent = (
    <DisplayContentBox>
      <OrganizationLabel variant='caption' color='text.secondary'>
        ORGANIZATION
      </OrganizationLabel>
      <OrganizationNameRow>
        {showBiotechIconForDisplayedOrg && (
          <BiotechIcon
            fontSize='small'
            color='action'
            sx={{ mr: 1, flexShrink: 0 }}
          />
        )}
        <OrganizationName
          variant='body2'
          noWrap
          sx={{ fontWeight: 600 }}
          data-testid='organization-name'
        >
          {displayName}
        </OrganizationName>
      </OrganizationNameRow>
    </DisplayContentBox>
  );

  // For clinician users: just show the display (non-interactive)
  if (!isCSOrSalesUser) {
    if (variant === 'drawer') {
      return (
        <Tooltip title={displayName} arrow placement='right'>
          <OrganizationContainerDrawer data-testid='organization-display'>
            {displayContent}
          </OrganizationContainerDrawer>
        </Tooltip>
      );
    }
    return (
      <Tooltip title={displayName} arrow placement='bottom'>
        <OrganizationContainer data-testid='organization-display'>
          {displayContent}
        </OrganizationContainer>
      </Tooltip>
    );
  }

  // For CS/Sales users: show interactive button with dropdown
  const ButtonComponent =
    variant === 'drawer' ? OrganizationButtonDrawer : OrganizationButton;

  return (
    <>
      <Tooltip title={displayName} arrow placement='bottom'>
        <ButtonComponent
          onClick={handleClick}
          data-testid='organization-button'
        >
          {displayContent}
          <StyledChevronRight isOpen={open} />
        </ButtonComponent>
      </Tooltip>
      {/* Only mount Menu when open so it unmounts immediately on close; prevents fast clicks
          from hitting a re-rendered list (e.g. after search clear) and selecting the wrong org. */}
      {open && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                width: 360,
                maxHeight: 500,
                mt: 1,
                scrollbarGutter: 'stable both-edges',
              },
            },
          }}
        >
          <SearchContainer>
            <SearchInput
              placeholder='Search organizations...'
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              data-testid='organization-search-input'
            />
          </SearchContainer>

          <ListContainer>
            {isLoading ? (
              <LoadingBox data-testid='organization-loading'>
                <CircularProgress size={24} />
              </LoadingBox>
            ) : filteredOrganizations.length === 0 ? (
              <EmptyStateBox data-testid='organization-empty-state'>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {searchTerm
                    ? 'No organizations found matching your search'
                    : 'No organizations available'}
                </Typography>
              </EmptyStateBox>
            ) : (
              <VirtualizedList
                items={filteredOrganizations}
                maxHeight={300}
                renderItem={(org: OrganizationListFieldsFragment) => (
                  <ListItem key={org.name} disablePadding>
                    <StyledListItemButton
                      onClick={() => handleOrganizationSelect(org)}
                      selected={org.name === organizationParam}
                      data-testid={`organization-item-${org.name}`}
                    >
                      {isDirectUploadLabOrganization(org) && (
                        <BiotechIcon
                          fontSize='small'
                          color='action'
                          sx={{ mr: 1, flexShrink: 0 }}
                        />
                      )}
                      <Tooltip
                        title={org.displayName ?? ''}
                        arrow
                        placement='left'
                      >
                        <OrganizationListItemText
                          primary={org.displayName}
                          slotProps={{
                            primary: {
                              noWrap: true,
                              sx: {
                                fontSize: theme.typography.body2.fontSize,
                              },
                            },
                          }}
                        />
                      </Tooltip>
                    </StyledListItemButton>
                  </ListItem>
                )}
              />
            )}
          </ListContainer>
        </Menu>
      )}
    </>
  );
};
